/* eslint-disable spire/export-styles */

import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import forgotPassword from "@insite/client-framework/Store/Context/Handlers/ForgotPassword";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { RefObject, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import recoverUsernamePassword from "@NA/Handlers/ADIRecoverUsernamePasswordHandler";
import ADIColors from "@NA/Utils/ADIColors";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import multiplevalidateReCaptcha from "@NA/Handlers/MultipleRecaptcha/ADIValidateReCaptchaHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ReCAPTCHA from "react-google-recaptcha";
import ReCaptcha from "@NA/Overrides/Components/ReCaptcha";
import ScrollToTop from "@NA/Components/ScrollToTop";

const enum fields {
    type = "type",
    textFieldValue = "textFieldValue",
    buttonFieldValue = "buttonFieldValue",
    captchaIds = "captchaIds",
}

const mapStateToProps = (state: ApplicationState) => {
    const { websiteSettings } = getSettingsCollection(state);   
    return {
        siteKey: websiteSettings.reCaptchaSiteKey,
        accountSettings: getSettingsCollection(state).accountSettings,
    };
};

interface CustomProps extends WidgetProps {
    fields: {
        [fields.type]: string;
        [fields.textFieldValue]: string;
        [fields.buttonFieldValue]: string;
        [fields.captchaIds]: string;
    };
}

const mapDispatchToProps = {
    resetPassword: forgotPassword,
    validateReCaptcha: makeHandlerChainAwaitable<any, boolean>(multiplevalidateReCaptcha),
    recoverUsernamePassword,
};

type Props = WidgetProps &
    CustomProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

export interface RecoverUserNameAndPasswordStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    title?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    enterEmailText?: TypographyPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldProps;
    reCaptchaGridItem?: GridItemProps;
    reCaptchaItem?: InjectableCss;
    buttonsGridItem?: GridItemProps;
    sendButton?: ButtonPresentationProps;
    returnLinkOnSuccess?: LinkPresentationProps;
    emailSentWrapper?: InjectableCss;
    reCaptchaError?: InjectableCss;
    emailErrorMsgText?: InjectableCss;
    scrollToTop?: InjectableCss;
}

export const recoverUserNameAndPasswordStyles: RecoverUserNameAndPasswordStyles = {
    emailSentWrapper: {
        css: css`
        display: flex;
        flex-direction: column;
        `
    },
    container: {
        css: css`
            background-color: ${ADIColors.gray6};
            @media only screen and (min-width: 1081px) {
                min-height: 440px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        width: 70%;
                        margin: 0 auto;
                    `,
                    null,
                ])}
        `,

        offsetCss: css`
            margin: 1rem auto;
            padding: 1.75rem 10.625rem 2.5rem 4.25rem;
            width: 100% !important;
            // min-height: 420px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        &:last-child {
                            margin-bottom: 20px;
                        }
                        padding: 1.25rem 0.5rem 1.75rem 0.5rem;
                        margin: 0 auto;
                    `,
                    css`
                        &:last-child {
                            margin-bottom: 20px;
                        }
                        padding: 1.25rem 0.5rem 1.75rem 0.5rem;
                        margin: 0 auto;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    userNameGridItem: {
        width: 12,
        css: css`
            padding: 0;
            
            @media only screen and (max-width: 1080px) {
                max-height: 75px;
            }
            input{
                max-width: 379px;
                &:focus{
                    padding: 9px 15px !important;
                }
            }
        `,
    },

    titleGridItem: {
        width: 12,
        css: css`
            padding: 0;            
        `,
    },
    title: {
        variant: "h3",
        css: css`
            margin-bottom: 8px;  
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        line-height: 1.08;
                        margin-bottom: 0px;
                        padding: 10px 0 8px 0;
                    `,
                    css`
                        line-height: 1.08;
                        margin-bottom: 0px;
                        padding: 10px 0 8px 0;
                    `,
                    null,
                    null,
                    null,
                ])}          
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    descriptionText: {
        css: css`
            // margin-bottom: 12px;
            font-weight: normal;
            line-height: 1.5;
            max-width: 460px;
            @media only screen and (max-width: 767px) {
                max-width: 320px;
            }
            @media only screen and (max-width: 1080px) {
                max-width: 365px;
            }
        `,
    },
    enterEmailText: {
        css: css`
        margin-bottom: 12px;
        font-weight: normal;
        line-height: 1.5;
        max-width: 355px;
        @media only screen and (max-width: 767px) {
            max-width: 320px;
        }
    `,
    },
    reCaptchaGridItem: {
        width: 12,
        css: css`
            padding: 0;
            margin: 29px 0 30px 0 !important;
            position: relative;
            @media only screen and (max-width: 1080px) {
                max-height: 71px;
                margin: 24px 0 25px 0 !important;                          
            }
        `,
    },
    reCaptchaItem: {
        css: css`
            transform: scale(0.84);
            transform-origin: 0px 0px;
            height: 63px;
        `,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            padding: 0;
            button{
                border-radius: 4px;
                min-width: 194px;
            }
        `,
    },
    returnLinkOnSuccess: {
        css: css`
            margin-top: 1.5rem;
            > ${TypographyStyle} {
                font-weight: bold;
            }
            &:hover {
                > ${TypographyStyle} {
                    color: ${ADIColors.blue5};
                    text-decoration: underline;
                }
            }
        `,
    },
    reCaptchaError: {
        css: css `
            color: #ff0000;
            font-size: 14px;
            display : block;
            position: relative;
            top : -15px;
            float: left;
        `
    },
    emailErrorMsgText: {
        css: css `
             color: #C60B2B;
             font-size: 13px;
             font-weight: normal;
        `
    },
    scrollToTop:{
        css: css`
       @media (min-width: 760px);
        `
    }
};

const styles = recoverUserNameAndPasswordStyles;

const ADIRecoverUserNameAndPassword = ({ validateReCaptcha, fields, recoverUsernamePassword, history, siteKey }: Props) => {
    const [userName, setUserName] = useState("");
    const [userNameError, setUserNameError] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [userCaptchaResponse, setUserCaptchaResponse] = useState("");
    const [passwordCaptchaResponse, setPasswordCaptchaResponse] = useState("");
    const [captchaSelectError, setCaptchaSelectError] = useState(false);
    const [emailErrorMsg, setemailErrorMsg] = useState(false);;
    const recaptchaRefUsername = useRef<ReCAPTCHA | null>(null);
    const recaptchaRefPassword = useRef<ReCAPTCHA | null>(null);

    const validateEmailAddressHandler = async (userName: string) => {
        if (!userName.trim()) {
            setUserNameError(true);
            return;
        }
        if (!validateEmail(userName)) {
            setUserNameError(true);
            return;
        }

        const captchaIds = fields.captchaIds ? fields.captchaIds.split(",") : [];

        setUserNameError(false);
        const currentVal = fields.type;
        if (currentVal === "Username") {
                setCaptchaSelectError(false);
                const isReCaptchaValid = await validateReCaptcha({ captchaIds });
                if (!isReCaptchaValid) {
                    return;
                }

                const grecaptcha = (window as { [key: string]: any })["grecaptcha"];
                if (grecaptcha === undefined) {
                    return;
                }

                if (recaptchaRefUsername.current) {
                    const handleUserVerifyCaptcha = recaptchaRefUsername?.current?.getValue();
                    if(handleUserVerifyCaptcha === userCaptchaResponse) {setUserCaptchaResponse(userCaptchaResponse);} 
                    else { setCaptchaSelectError(true);setUserCaptchaResponse("")}  
                }

                recoverUsernamePassword({
                    userEmail: userName,
                    passwordEmail: userName, 
                    isPasswordReset: false,
                    isuserNameReset: true,
                    userNotFound: false,
                    isSuccess: false,
                    uri: "",
                    properties: { captchaResponse: userCaptchaResponse },
                    onSuccess: (result: any) => {
                        if (result.errorMessage) {
                            setCaptchaSelectError(true);
                        }
                        else if (result.successful && result.result.userNotFound) {
                            setemailErrorMsg(true);
                            setCaptchaSelectError(true);
                            recaptchaRefUsername?.current?.reset();
                        }
                        else if (result.successful && result.result.isSuccess) {
                            setCaptchaSelectError(false);
                            setemailErrorMsg(false);
                            setUserCaptchaResponse("");
                            setShowSuccessMessage(true);
                        }
                    },
                });

        } else if(currentVal === "Password") {
            const isReCaptchaValidated = await validateReCaptcha({ captchaIds });
            if (!isReCaptchaValidated) {
                 return;
            }

            const grecaptchaPwd = (window as { [key: string]: any })["grecaptcha"];
            if (grecaptchaPwd === undefined) {
                return;
            }

            if (recaptchaRefPassword.current) {
                  const handlePwdVerifyCaptcha = recaptchaRefPassword?.current?.getValue();
                  if(handlePwdVerifyCaptcha === passwordCaptchaResponse) {setPasswordCaptchaResponse(passwordCaptchaResponse);} 
                  else {setPasswordCaptchaResponse('');}  
            }

            recoverUsernamePassword({
                userEmail: userName, 
                passwordEmail: userName,
                isPasswordReset: true,
                isuserNameReset: false,
                userNotFound: false,
                isSuccess: false,
                uri: "",
                properties: { captchaResponse: passwordCaptchaResponse},

                onSuccess: (result: any) => {
                    if (result.errorMessage) {
                        setCaptchaSelectError(true);
                    }
                    else if (result.successful && result.result.userNotFound) {
                        setemailErrorMsg(true);
                       setCaptchaSelectError(true);
                       recaptchaRefPassword?.current?.reset();
                    }
                    else if (result.successful && result.result.isSuccess) {
                        setCaptchaSelectError(false);
                        setemailErrorMsg(false);
                        setPasswordCaptchaResponse("");
                        setShowSuccessMessage(true);
                    }
                },
            }); 
        }
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newUsername = e.currentTarget.value;
        setUserName(newUsername);
        setUserNameError(false);
    };

    const returnOnSuccessHandler = () => {
        history.push("/MyAccount/recoverusernamepassword");
    };

    const handleUsernameVerifyCaptcha = (token: string | null) => {
        if (token) {
            setUserCaptchaResponse(token); 
        } 
    };
    const handleUserExpiredCaptcha = () => {
        setUserCaptchaResponse("");
    };

    const handlePasswordVerifyCaptcha = (token: string | null) => {
        if (token) {
            setPasswordCaptchaResponse(token); 
        } 
    };
    const handlePasswordExpiredCaptcha = () => {
        setPasswordCaptchaResponse("");
    };

    return (
        <><GridContainer {...styles.container}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.title}>{fields.textFieldValue}</Typography>
            </GridItem>
            <GridItem {...styles.descriptionGridItem}>
                {showSuccessMessage ? (
                    <StyledWrapper {...styles.emailSentWrapper}>
                        <Typography as="p" {...styles.descriptionText}>
                            {translate(`An email has been sent to the email address you entered. Please click on the link in the email to ${fields.type === "Username" ? "recover your username" : "reset your password"}. If you need additional assistance, please call Website Support at 1.877.228.6739.`)}
                        </Typography>
                        <Link
                            {...styles.returnLinkOnSuccess}
                            href="/MyAccount/recoverusernamepassword"
                            onClick={returnOnSuccessHandler}
                        >
                            {translate("Need to enter your email address again?")}
                        </Link>
                    </StyledWrapper>

                ) : (
                    <>
                        {fields.type === "Username" ? (
                            <Typography as="p" {...styles.enterEmailText}>
                                {translate(
                                    "Enter your email address and we'll send you an email with a link to recover your lost username.",
                                )}
                            </Typography>
                        ) : (
                            <Typography as="p" {...styles.enterEmailText}>
                                {translate(
                                    "Enter your email address and we'll send you an email with a link to reset your password.",
                                )}
                            </Typography>
                        )}
                    </>
                )}
            </GridItem>
            { emailErrorMsg && !showSuccessMessage && (
                <Typography as="p" {...styles.emailErrorMsgText}>
                    {translate(
                        "If the username matches an account, an email will be sent to reset password.",
                    )}
                </Typography>
            )}
            <GridItem {...styles.userNameGridItem}>
                {!showSuccessMessage && (
                    <TextField
                        {...styles.userNameTextField}
                        label={translate("Email Address")}
                        name="userName"
                        value={userName}
                        placeholder={translate("Enter your email address")}
                        onChange={handleUsernameChange}
                        data-test-selector="resetPassword_userName"
                        required
                        type="email"
                        style={{ borderColor: userNameError ? ADIColors.danger : ADIColors.gray3 }}
                    />
                )}
            </GridItem>
            <GridItem {...styles.reCaptchaGridItem}>
                {!showSuccessMessage && (
                     <>
                         <StyledWrapper {...styles.reCaptchaItem}> 
                            {fields.type === "Username" && ( 
                                <ReCAPTCHA
                                     sitekey={siteKey}
                                     onExpired={handleUserExpiredCaptcha}
                                     onChange={handleUsernameVerifyCaptcha} 
                                     ref={recaptchaRefUsername} 
                                /> 
                            )}
                        </StyledWrapper>
                    
                        {fields.type.toLowerCase() === "password" && (
                            <>
                            <StyledWrapper {...styles.reCaptchaItem}> 
                                <ReCAPTCHA
                                    sitekey={siteKey}
                                    onExpired={handlePasswordExpiredCaptcha}
                                    onChange={handlePasswordVerifyCaptcha} 
                                    ref={recaptchaRefPassword}
                                />
                            </StyledWrapper>
                            </>
                        )}
                    </>
                )}
            </GridItem>
            {captchaSelectError && (
                <Typography as="p" {...styles.reCaptchaError}>
                    {translate("Please Validate the Captcha.")}
                </Typography>
            )}

            <GridItem {...styles.buttonsGridItem}>
                {!showSuccessMessage && (
                    <Button
                        {...styles.sendButton}
                        onClick={() => validateEmailAddressHandler(userName)}
                        data-test-selector="resetPassword_submit"
                    >
                        {fields.buttonFieldValue}
                    </Button>
                )}
            </GridItem>
        </GridContainer>
        <GridItem {...styles.scrollToTop}>
            <ScrollToTop />
            </GridItem>
        </>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ADIRecoverUserNameAndPassword)),
    definition: {
        group: "Basic",
        icon: "PageTitle",
        displayName: "Recover UserName And Password",
        fieldDefinitions: [
            {
                name: fields.type,
                displayName: "Type",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.textFieldValue,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },

            {
                name: fields.captchaIds,
                displayName: "Captcha Id",
                editorTemplate: "TextField",
                defaultValue: "reCaptcha1",
                fieldType: "General",
            },
            {
                name: fields.buttonFieldValue,
                displayName: "Button Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;

